<script>
    import { onMount } from "svelte";
    import { closeModal } from "svelte-modals";
    import { fly } from "svelte/transition";
    import toast from "svelte-french-toast";
    import SearchBar from "../SearchBars/SearchBar.svelte";
    import Button from "../Button/Button.svelte";
    import { getGroups } from "../../services/groupService";
    import { reassignMemberships } from "../../services/jurisdictionService";

    export let isOpen;
    export let groupId = undefined;
    export let onConfirm;
    export let selectedIds = [];
    export let title = "Seleziona le federazioni";

    let federations = [];
    let selected = new Map(); // id -> federation
    let isLoading = true;
    let isPressed = false;
    let searchText = "";

    function loadFederations() {
        isLoading = true;

        const params = {
            page: 1,
            size: 50,
            groupType: "FEDERATION",
            ...(searchText ? { name: searchText } : {})
        };

        getGroups(params).then((resp) => {
            federations = resp?.data?.data ?? [];

            // keep already-selected entries highlighted even if not in the list
            const known = new Map(selected);
            federations
                .filter((f) => selectedIds.includes(f.id))
                .forEach((f) => known.set(f.id, f));
            selected = known;
        }).catch((err) => {
            toast.error(`Errore durante il caricamento delle federazioni, errore ${err.toString()}`);
        }).finally(() => {
            isLoading = false;
        });
    }

    function toggle(federation) {
        const next = new Map(selected);
        next.has(federation.id) ? next.delete(federation.id) : next.set(federation.id, federation);
        selected = next;
    }

    function onSearch(event) {
        searchText = event.detail;
        loadFederations();
    }

    function confirm() {
        if (!selected.size || isPressed) {
            return;
        }

        isPressed = true;

        reassignMemberships({ groupId, fromGroupIds: [...selected.keys()] }).then((resp) => {
            isPressed = false;

            if (resp.status === 200) {
                const count = resp?.data?.reassignedCount ?? 0;
                toast.success(
                    count === 1
                        ? "1 tessera riassegnata"
                        : `${count} tessere riassegnate`
                );

                onConfirm?.([...selected.values()]);
                closeModal();
            }
        }).catch((err) => {
            isPressed = false;
            toast.error(`Impossibile riassegnare le tessere, errore ${err.toString()}`);
        });
    }

    onMount(loadFederations);
</script>

{#if isOpen}
    <div
        class="large-modal w-11/12 md:w-7/12"
        transition:fly={{ y: -100 }}
        on:introstart
        on:outroend
    >
        <div class="flex flex-col gap-1">
            <div class="text-3xl font-bold">{title}</div>
            <div class="text-xl text-blueGray-500">
                Le tessere delle federazioni selezionate verranno aggiornate in base alla giurisdizione.
            </div>
        </div>

        <SearchBar
            placeholder="Filtra per nome della federazione"
            hasLabel={false}
            value={searchText}
            on:search={onSearch}
        />

        <div class="list-area">
            {#if isLoading}
                <div class="flex items-center justify-center h-32 text-blueGray-400">Caricamento…</div>
            {:else if !federations.length}
                <div class="flex items-center justify-center h-32 text-blueGray-400">Nessuna federazione trovata</div>
            {:else}
                {#each federations as federation (federation.id)}
                    <label class="flex items-center gap-3 py-2 px-2 border-b cursor-pointer hover:bg-blueGray-50">
                        <input
                            type="checkbox"
                            class="cursor-pointer w-5 h-5"
                            checked={selected.has(federation.id)}
                            on:change={() => toggle(federation)}
                        />
                        <span class="font-semibold">{federation.name}</span>
                    </label>
                {/each}
            {/if}
        </div>

        <div class="flex flex-col gap-1">
            <div class="font-bold text-lg">
                Federazioni selezionate{selected.size ? ` (${selected.size})` : ""}:
            </div>

            <div class="w-full flex flex-wrap items-center gap-2 py-2 max-h-32 overflow-y-auto">
                {#if !selected.size}
                    <div class="w-full flex items-center h-10 text-blueGray-400">Nessuna federazione selezionata</div>
                {:else}
                    {#each [...selected.values()] as federation (federation.id)}
                        <div class="py-1 px-3 bg-gray-200 rounded-full w-max">
                            {federation.name}
                        </div>
                    {/each}
                {/if}
            </div>
        </div>

        <div class="flex items-center justify-end gap-4">
            <div class="flex gap-2">
                <Button
                    handleClick={confirm}
                    isDisabled={!selected.size || isPressed}
                    buttonClass="bg-lightBlue-500 text-center font-semibold text-white px-6"
                    content="Conferma"
                />

                <Button
                    handleClick={closeModal}
                    buttonClass="bg-blueGray-600 text-center font-semibold text-white px-6"
                    content="Annulla"
                />
            </div>
        </div>
    </div>
{/if}

<style>
    .large-modal {
        border-radius: 5px;
        padding: 15px;
        position: fixed;
        background-color: white;
        z-index: 85;
        left: 50%;
        top: 3rem;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 1rem;
        translate: -50%;
        overflow-y: auto;
        max-height: 85%;
    }

    .list-area {
        max-height: 16rem;
        overflow-y: auto;
        width: 100%;
    }
</style>
